import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLists } from '../contexts/ListContext';
import { useProducts } from '../contexts/ProductsContext';
import { Box, Typography, Button } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { Product } from '../types/product';
import { ProductCard } from '../components/ProductCard';

const ListPage: React.FC = () => {
  const { listId } = useParams<{ listId: string }>();
  const { getList } = useLists();
  const { products, loading } = useProducts();
  const navigate = useNavigate();
  const [listProducts, setListProducts] = useState<Product[]>([] as Product[]);
  const [list, setList] = useState<ReturnType<typeof getList>>(undefined);

  useEffect(() => {
    if (!listId) return;
    
    const currentList = getList(listId);
    setList(currentList);
    
    if (currentList && products.length > 0) {
      const listItems = products.filter(product => 
        currentList.productIds.includes(product.id)
      );
      setListProducts(listItems);
    }
  }, [listId, getList, products]);

  if (!list) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h6">List not found</Typography>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate('/favorites')}
          sx={{ mt: 2 }}
        >
          Back to Favorites
        </Button>
      </Box>
    );
  }

  // Loading skeletons while fetching products
  if (loading) {
    const skeletons = Array.from({ length: 12 });
    return (
      <Box p={3}>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" gutterBottom>
          {list.name}
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(4, 1fr)',
            xl: 'repeat(4, 1fr)'
          },
          gap: 3,
          width: '100%'
        }}>
          {skeletons.map((_, idx) => (
            <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Skeleton variant="rectangular" height={320} />
              <Box sx={{ p: 2 }}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="40%" />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Back
      </Button>
      
      <Typography variant="h4" gutterBottom>
        {list.name}
      </Typography>
      
      <Typography variant="body1" color="textSecondary" paragraph>
        {list.productIds.length} {list.productIds.length === 1 ? 'item' : 'items'}
      </Typography>
      
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
          lg: 'repeat(4, 1fr)',
          xl: 'repeat(4, 1fr)'
        },
        gap: 3,
        width: '100%'
      }}>
        {listProducts.map((product) => (
          <ProductCard 
            className="product-card"
            key={product.id}
            product={product}
          />
        ))}
        
        {listProducts.length === 0 && (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="textSecondary">
                This list is empty
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                Add products to this list using the "Add to List" button on product pages.
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => navigate('/products')}
              >
                Browse Products
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ListPage;
